import { cn } from '@/utils/utils.ts';
import { FileText } from 'lucide-react';
import { useParams } from 'react-router';
import {
  Credenza,
  CredenzaContent,
  CredenzaDescription,
  CredenzaHeader,
  CredenzaTitle,
  CredenzaTrigger,
} from '@/components/ui/credenza.tsx';
import { Suspense, useState } from 'react';
import { useGetPlantQuery } from '@/features/plants/hooks/queries/[plantId]/use-get-plant.ts';
import { LoadingSpinner } from '@/components/ui/loading-spinner.tsx';
import { lazyWithPreload } from '@/utils/lazy-with-preload.ts';

const PlantMenuPopoverGenerateReportDialogBody = lazyWithPreload(
  () =>
    import('@/features/plants/components/plant-overview/plant-menu-popover-generate-report-dialog-body.tsx')
);

export function PlantMenuPopoverGenerateReportDialog({ plantId }: { plantId?: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const { plantId: paramPlantId } = useParams();
  const effectivePlantId = plantId ?? paramPlantId;

  const { data: plant } = useGetPlantQuery({ plantId: effectivePlantId });

  const closeDialog = () => setIsOpen(false);

  return (
    <Credenza open={isOpen} onOpenChange={setIsOpen}>
      <CredenzaTrigger asChild>
        <button
          className={cn(
            'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm',
            'text-left',
            'hover:bg-accent hover:text-accent-foreground',
            'focus-visible:bg-accent focus-visible:text-accent-foreground focus-visible:outline-none',
            'disabled:pointer-events-none disabled:opacity-50'
          )}
          role="menuitem"
        >
          <FileText className="size-4" />
          Generate Report
        </button>
      </CredenzaTrigger>
      <CredenzaContent className={cn('min-h-[518px] sm:min-h-[468px] sm:min-w-[593px]')}>
        <CredenzaHeader>
          <CredenzaTitle>{`Generate ${plant?.basicDetails.name} report`}</CredenzaTitle>
          <CredenzaDescription>
            Generate and export a report for plant as a PDF file.
          </CredenzaDescription>
        </CredenzaHeader>
        <div className="h-full flex-1">
          <Suspense
            fallback={
              <div className="flex min-h-[300px] w-full items-center justify-center">
                <LoadingSpinner size={52} />
              </div>
            }
          >
            <PlantMenuPopoverGenerateReportDialogBody
              closeDialog={closeDialog}
              plantId={effectivePlantId}
            />
          </Suspense>
        </div>
      </CredenzaContent>
    </Credenza>
  );
}
